import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const statusColor = (s) => {
  if (s === 'approved') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'rejected' || s === 'suspended') return 'error';
  return 'default';
};

const NavigatorsAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [assignOpen, setAssignOpen] = useState(null);
  const [town, setTown] = useState('');
  const [district, setDistrict] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/navigator/admin/navigators`, {
          headers: authHeaders(),
          params: { status: status || undefined, q: q || undefined },
        }),
        axios.get(`${API_BASE_URL}/navigator/admin/reports/summary`, {
          headers: authHeaders(),
        }),
      ]);
      setItems(listRes.data?.data?.items || []);
      setSummary(sumRes.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load Navigators');
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    try {
      await axios.post(
        `${API_BASE_URL}/navigator/admin/navigators/${id}/${action}`,
        {},
        { headers: authHeaders() },
      );
      toast.success(`${action} ok`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed');
    }
  };

  const saveAssign = async () => {
    if (!assignOpen) return;
    try {
      await axios.patch(
        `${API_BASE_URL}/navigator/admin/navigators/${assignOpen.id}`,
        { assignedTown: town, assignedDistrict: district },
        { headers: authHeaders() },
      );
      toast.success('Territory updated');
      setAssignOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed');
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/admin-control-screens"
            startIcon={<ArrowBackIcon />}
            size="small"
          >
            Hub
          </Button>
          <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
            Loksy Navigators
          </Typography>
          <Button
            component={RouterLink}
            to="/content/nav-app-controls"
            size="small"
          >
            Nav App Controls
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
        </Stack>

        {summary && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Navigators: {summary.navigators} · Pending: {summary.pending} ·
            Attributions: {summary.attributions} · Commission ₹
            {((summary.commissionsPaise || 0) / 100).toFixed(0)}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Search name / phone / town"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              fullWidth
            />
            <Button variant="outlined" onClick={load}>
              Search
            </Button>
          </Stack>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Town</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((n) => (
                  <TableRow key={n.id}>
                    <TableCell>{n.displayName}</TableCell>
                    <TableCell>{n.phone}</TableCell>
                    <TableCell>
                      {n.assignedTown || n.town || '—'}
                      {n.assignedDistrict || n.district
                        ? ` / ${n.assignedDistrict || n.district}`
                        : ''}
                    </TableCell>
                    <TableCell>{n.referralCode}</TableCell>
                    <TableCell>
                      <Chip size="small" label={n.status} color={statusColor(n.status)} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {n.status === 'pending' && (
                          <>
                            <Button size="small" onClick={() => act(n.id, 'approve')}>
                              Approve
                            </Button>
                            <Button size="small" color="error" onClick={() => act(n.id, 'reject')}>
                              Reject
                            </Button>
                          </>
                        )}
                        {n.status === 'approved' && (
                          <Button size="small" color="warning" onClick={() => act(n.id, 'suspend')}>
                            Suspend
                          </Button>
                        )}
                        {(n.status === 'suspended' || n.status === 'rejected') && (
                          <Button size="small" onClick={() => act(n.id, 'activate')}>
                            Activate
                          </Button>
                        )}
                        <Button
                          size="small"
                          onClick={() => {
                            setAssignOpen(n);
                            setTown(n.assignedTown || n.town || '');
                            setDistrict(n.assignedDistrict || n.district || '');
                          }}
                        >
                          Territory
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        No Navigators found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}

        <Dialog open={!!assignOpen} onClose={() => setAssignOpen(null)} fullWidth maxWidth="xs">
          <DialogTitle>Assign territory</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Town"
                size="small"
                value={town}
                onChange={(e) => setTown(e.target.value)}
                fullWidth
              />
              <TextField
                label="District"
                size="small"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignOpen(null)}>Cancel</Button>
            <Button variant="contained" onClick={saveAssign}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default NavigatorsAdmin;
